import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus, ArrowRight, ChevronDown, Star } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { cn } from "@/lib/utils";
import { PreviewHeader as SharedPreviewHeader, PreviewFooter as SharedPreviewFooter, type FooterColumn, type ResourcesMenu } from "@/components/site/PreviewChrome";
import { REVIEWS, REVIEWS_META } from "@/lib/reviews";
import { Marquee } from "@/components/site/Marquee";
import { quotePath, quoteSearch, type ScopeIntentId } from "@/lib/quote-links";

// Zebras / stickers
import zebraHero from "@/assets/zebra-hero.webp";
import zebraPortrait from "@/assets/zebra-portrait.webp";
import zebraSimple from "@/assets/zebra-simple.webp";
import ctaZebra from "@/assets/cta-zebra-256.webp";
import zebraSteps from "@/assets/brand/accent-zebra-steps.png";
import zebraFaq from "@/assets/brand/accent-zebra-faq.png";
import zebraTestimonial from "@/assets/brand/accent-zebra-testimonial.png";
import geoScore from "@/assets/brand/geo-zebra-score.png";
import geoCitations from "@/assets/brand/geo-zebra-citations.png";
import geoRoadmap from "@/assets/brand/geo-zebra-roadmap.png";

// Accent glyphs
import accentLeaf from "@/assets/brand/accent-leaf.svg";
import handPointer from "@/assets/brand/icon-hand-pointer.svg";
import arrowRight from "@/assets/brand/accent-arrow-right.png";
import cloudDownload from "@/assets/brand/accent-cloud-download.png";
import urgentBolt from "@/assets/brand/accent-urgent-bolt.png";
import urgentExclam from "@/assets/brand/accent-urgent-exclamation.png";
import airplaneUp from "@/assets/brand/accent-airplane-up.png";
import airplaneDown from "@/assets/brand/accent-airplane-down.png";
import airplaneLegend from "@/assets/brand/accent-airplane-legend.png";
import giraffe from "@/assets/brand/brand-giraffe.svg";

// Shopify illustrations for phase cards
import shopifyDev from "@/assets/brand/shopify-development.png";
import shopifyCart from "@/assets/brand/shopify-cart.png";
import shopifyAi from "@/assets/brand/shopify-ai-analytics.png";

// Client logos
import boletaje from "@/assets/clients/boletaje.webp";
import butcherjames from "@/assets/clients/butcherjames.webp";
import boxchois from "@/assets/clients/boxchois.webp";
import fashionablecanes from "@/assets/clients/fashionablecanes.webp";
import medshift from "@/assets/clients/medshift.webp";
import classy from "@/assets/clients/classywalkingcanes.webp";
import cleveland from "@/assets/clients/clevelandkitchen.webp";
import pokerpower from "@/assets/clients/pokerpower.webp";
import redondo from "@/assets/clients/redondobrand.webp";
import selfcare from "@/assets/clients/selfcareisforeveryone.webp";
import threadseed from "@/assets/clients/threadseed.webp";
import oomame from "@/assets/clients/oomame.webp";

// Featured-in logos
import forbesLogo from "@/assets/forbes-logo.webp";
import techbehemothsLogo from "@/assets/techbehemoths-logo.webp";
import apLogo from "@/assets/ap-logo.svg";
import europapressLogo from "@/assets/europapress-logo.webp";
import lavanguardiaLogo from "@/assets/lavanguardia-logo.svg";

export const Route = createFileRoute("/home-preview")({
  head: () => ({
    meta: [
      { title: "Home Preview: Shugert Sandbox" },
      { name: "description", content: "Internal design exploration sandbox. Not linked publicly." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: HomePreviewRoute,
});

type SceneTone = "navy" | "electric" | "sky" | "white" | "yellow";
type PreviewLocale = "en" | "es";
type PreviewCopy = ReturnType<typeof getCopy>;

function getCopy(locale: PreviewLocale) {
  if (locale === "es") {
    return {
      quoteHref: "/es/cotizacion" as const,
      lang: "ES",
      otherLang: "EN",
      getQuote: "Cotizar",
      nav: {
        work: { label: "Proyectos", to: "/es/proyectos" as const },
        services: { label: "Servicios", to: "/es/servicios" as const },
        pricing: { label: "Precios", to: "/es/precios" as const },
        resources: { label: "Recursos", to: "/es/recursos" as const },
        about: { label: "Nosotros", to: "/es/nosotros" as const },
      },
      servicesMenu: [
        ["Desarrollo Shopify", "/es/desarrollo-shopify"],
        ["Agencia Shopify Plus", "/es/agencia-shopify-plus"],
        ["Rendimiento", "/es/rendimiento"],
        ["CRO Shopify", "/es/cro-shopify"],
        ["SEO Técnico", "/es/seo-tecnico-shopify"],
        ["Analítica & IA", "/es/analitica-ai"],
        ["GEO Readiness", "/geo-readiness"],
        ["PULSE", "/es/pulse"],
      ] as const,
      heroEyebrow: "Shopify Select Partner · Desde 2015",
      heroLine1: "Tu tienda Shopify,",
      heroLine2: "ingeniería que vende.",
      heroSub:
        "Diseñamos, construimos y escalamos tiendas Shopify Plus para marcas DTC consolidadas. Tres oficinas. Más de 750 proyectos Shopify completados. Alcance fijo, sin humo de agencia.",
      heroCta: "Empieza tu proyecto",
      heroScroll: "baja para ver las pruebas",
      reviewsEyebrow: "Reseñas verificadas",
      reviewsTitle: "Lo que dicen nuestros clientes.",
      reviewsAll: "Ver todas en Shopify Partner Directory",
      featured: "Como nos han visto",
      rosterEyebrow: "El roster",
      rosterTitle: "Marcas que enviamos.",
      howEyebrow: "Cómo trabajamos",
      howTitle: "Un proceso claro, sin sorpresas.",
      howSteps: [
        ["Detectar el cuello de botella", "Auditamos rendimiento, conversión y fugas de revenue antes de proponer alcance."],
        ["Definir un alcance claro", "Entregables, plazos y precio fijos. Sin sorpresas a mitad de proyecto."],
        ["Ejecutar con impacto medible", "Lanzamos en semanas, con demos semanales y métricas que mapean a revenue."],
        ["Acompañar el crecimiento", "Retainer opcional para CRO, performance e ingeniería continua."],
      ] as const,
      pricingEyebrow: "Precios",
      pricingTitle: "Alcance y precio claros desde el inicio.",
      pricingFooter: "Todos los alcances confirmados por escrito en 1 día hábil.",
      pricingSeeAll: "Ver precios completos →",
      pricingCards: [
        ["Proyectos a alcance fijo", "Desde $1,500", "Fixes de performance, sprints de CRO, builds custom."],
        ["Retainer mensual", "Desde $5,000/mes", "Growth & Engineering senior; ingeniería Plus desde $9,000/mes."],
        ["Auditoría diagnóstica", "Desde $750", "Revisión liderada por seniors con plan de acción priorizado."],
      ] as const,
      isThisYou: {
        eyebrow: "¿ERES TÚ?",
        title: "Arregla lo que frena tu tienda Shopify",
        qualifier: "$500k+ en ingresos anuales",
        rows: [
          "Tiempos de carga lentos",
          "Fugas de revenue en páginas de producto",
          "Estancados pese al crecimiento de tráfico",
          "Demasiadas apps e inestabilidad",
        ] as const,
      },
      resourcesMenu: {
        pillarsLabel: "GUÍAS PILAR",
        pillars: [
          { title: "Guía de Optimización de Rendimiento Shopify", desc: "Velocidad, Core Web Vitals y render path.", href: "/es/recursos/optimizacion-rendimiento-shopify" },
          { title: "Masterclass de Migración a Shopify Plus", desc: "Replatformar desde BigCommerce, Magento o Woo.", href: "/es/recursos/migracion-shopify-plus" },
          { title: "Playbook de CRO Shopify", desc: "PDP, checkout y size guide.", href: "/es/recursos/cro-shopify" },
          { title: "Guía de Estrategia de Marketing Shopify", desc: "5 capas, plan de 90 días, mix de canales.", href: "/es/recursos/estrategia-marketing-shopify" },
        ],
        topicsLabel: "EXPLORAR POR TEMA",
        topics: [
          { title: "Hub de Rendimiento", desc: "Velocidad, Core Web Vitals, render path.", href: "/es/recursos/tag/performance" },
          { title: "Hub de Migración", desc: "BigCommerce, Magento y Woo a Shopify.", href: "/es/recursos/tag/migracion" },
          { title: "Hub de Conversión", desc: "CRO, PDP, checkout y size guide.", href: "/es/recursos/tag/cro" },
          { title: "Todos los recursos", desc: "Artículos, playbooks y field notes.", href: "/es/recursos" },
        ],
        featured: {
          badge: "NUEVO",
          title: "Guía de Estrategia de Marketing Shopify",
          subtitle: "La guía pilar: 5 capas, plan de 90 días, mix de canales.",
          cta: "Leer",
          href: "/es/recursos/estrategia-marketing-shopify",
        },
      },
    };
  }
  return {
    quoteHref: "/get-a-quote" as const,
    lang: "EN",
    otherLang: "ES",
    getQuote: "Get a quote",
    nav: {
      work: { label: "Work", to: "/work" as const },
      services: { label: "Services", to: "/services" as const },
      pricing: { label: "Pricing", to: "/pricing" as const },
      resources: { label: "Resources", to: "/resources" as const },
      about: { label: "About", to: "/about" as const },
    },
    servicesMenu: [
      ["Shopify Development", "/shopify-experts"],
      ["Shopify Plus Agency", "/shopify-plus-agency"],
      ["Performance", "/shopify-performance"],
      ["Shopify CRO", "/shopify-cro"],
      ["Technical SEO", "/shopify-technical-seo"],
      ["Analytics & AI", "/analytics-ai"],
      ["GEO Readiness", "/geo-readiness"],
      ["PULSE", "/pulse"],
    ] as const,
    heroEyebrow: "Shopify Select Partner · Since 2015",
    heroLine1: "Your Shopify store,",
    heroLine2: "engineered to sell.",
    heroSub:
      "We design, build, and scale Shopify Plus stores for established DTC brands. Three offices. 750+ Shopify engagements completed. Fixed scope, no agency fluff.",
    heroCta: "Start your build",
    heroScroll: "scroll for the proof",
    reviewsEyebrow: "Verified reviews",
    reviewsTitle: "What clients say.",
    reviewsAll: "View all on Shopify Partner Directory",
    featured: "As featured in",
    rosterEyebrow: "The roster",
    rosterTitle: "Brands we ship for.",
    howEyebrow: "How we work",
    howTitle: "A clear process, no surprises.",
    howSteps: [
      ["Identify the bottleneck", "Audit performance, conversion, and revenue leaks before scoping anything."],
      ["Define a clear scope", "Fixed deliverables, fixed timeline, fixed price. No surprises mid-project."],
      ["Execute with measurable impact", "Ship in weeks with weekly demos and metrics that map to revenue."],
      ["Support ongoing growth", "Optional retainer for continuous CRO, performance, and engineering."],
    ] as const,
    pricingEyebrow: "Pricing",
    pricingTitle: "Scoped & priced upfront.",
    pricingFooter: "All scopes confirmed in writing within 1 business day.",
    pricingSeeAll: "See full pricing →",
    pricingCards: [
      ["Fixed-scope projects", "From $1,500", "Performance fixes, CRO sprints, custom builds."],
      ["Monthly retainer", "From $5,000/mo", "Senior Growth & Engineering; Plus engineering from $9,000/mo."],
      ["Diagnostic audit", "From $750", "Senior-led review with a prioritized action plan."],
    ] as const,
    isThisYou: {
      eyebrow: "IS THIS YOU?",
      title: "Fix what's slowing down your Shopify store",
      qualifier: "$500k+ in annual revenue",
      rows: [
        "Struggling with slow load times",
        "Leaking revenue on product pages",
        "Stuck despite traffic growth",
        "Dealing with too many apps and instability",
      ] as const,
    },
    resourcesMenu: {
      pillarsLabel: "PILLAR GUIDES",
      pillars: [
        { title: "Shopify Performance Optimization Guide", desc: "Speed, Core Web Vitals and the render path.", href: "/resources/shopify-performance-optimization" },
        { title: "Shopify Plus Migration Masterclass", desc: "Replatform from BigCommerce, Magento or Woo.", href: "/resources/shopify-plus-migration" },
        { title: "Shopify CRO Playbook", desc: "PDP, checkout and size guide playbook.", href: "/resources/shopify-cro-playbook" },
        { title: "Shopify Marketing Strategy Guide", desc: "5 layers, 90-day plan, channel mix.", href: "/resources/shopify-marketing-strategy" },
      ],
      topicsLabel: "BROWSE BY TOPIC",
      topics: [
        { title: "Performance hub", desc: "Speed, Core Web Vitals, render path.", href: "/resources/tag/performance" },
        { title: "Migration hub", desc: "BigCommerce, Magento and Woo to Shopify.", href: "/resources/tag/migration" },
        { title: "Conversion hub", desc: "CRO, PDP, checkout and size guide.", href: "/resources/tag/cro" },
        { title: "All resources", desc: "Articles, playbooks and field notes.", href: "/resources" },
      ],
      featured: {
        badge: "NEW",
        title: "Shopify Marketing Strategy Guide",
        subtitle: "The pillar guide: 5 layers, 90-day plan, channel mix.",
        cta: "Read",
        href: "/resources/shopify-marketing-strategy",
      },
    },
  };
}

const SCOPED_CSS = `
.preview-root {
  --pv-navy: #1f1f1f;
  --pv-electric: #1E5BFF;
  --pv-sky: #7BB7FF;
  --pv-white: #f2f2f0;
  --pv-yellow: #FFE94A;
  --pv-ink: #051026;
  background-color: var(--pv-white);
  transition: background-color 700ms ease;
  color: var(--pv-ink);
}
.preview-root[data-scene="navy"]     { background-color: var(--pv-navy); }
.preview-root[data-scene="electric"] { background-color: var(--pv-electric); }
.preview-root[data-scene="sky"]      { background-color: var(--pv-sky); }
.preview-root[data-scene="white"]    { background-color: var(--pv-white); }
.preview-root[data-scene="yellow"]   { background-color: var(--pv-yellow); }

.preview-root .pv-scene { position: relative; overflow: hidden; isolation: isolate; }
.preview-root .pv-scene-navy     { background: radial-gradient(120% 80% at 20% 10%, #143464 0%, var(--pv-navy) 60%); color: #fff; }
.preview-root .pv-scene-electric { background: linear-gradient(135deg, #1E5BFF 0%, #2E6BFF 50%, #1f1f1f 110%); color: #fff; }
.preview-root .pv-scene-sky      { background: linear-gradient(180deg, #CFE3FF 0%, #A8C8FF 100%); color: var(--pv-ink); }
.preview-root .pv-scene-sky-hero { background: linear-gradient(180deg, #D8E8FF 0%, #B6D0FF 100%); color: var(--pv-ink); }
.preview-root .pv-scene-white    { background: var(--pv-white); color: var(--pv-ink); }
.preview-root .pv-scene-yellow   { background: var(--pv-yellow); color: var(--pv-ink); }

@keyframes pv-orb { 0%,100% { transform: translate3d(0,0,0) scale(1);} 50% { transform: translate3d(6%, -4%, 0) scale(1.15);} }
@keyframes pv-grad-shift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
@keyframes pv-marquee-rev { from { transform: translateX(-50%); } to { transform: translateX(0); } }
@keyframes pv-float { 0%,100% { transform: translateY(0) rotate(var(--r, 0deg)); } 50% { transform: translateY(-14px) rotate(calc(var(--r, 0deg) + 2deg)); } }
@keyframes pv-spin-slow { to { transform: rotate(360deg); } }

.preview-root .pv-orb {
  position: absolute; inset: -10% -10% auto auto; width: 70vmin; height: 70vmin;
  background: radial-gradient(circle, rgba(123,183,255,0.55) 0%, rgba(30,91,255,0) 60%);
  filter: blur(20px); animation: pv-orb 18s ease-in-out infinite; z-index: 0; pointer-events: none;
}
.preview-root .pv-orb-2 {
  position: absolute; inset: auto auto -20% -10%; width: 60vmin; height: 60vmin;
  background: radial-gradient(circle, rgba(123,183,255,0.65) 0%, rgba(10,37,64,0) 65%);
  filter: blur(30px); animation: pv-orb 24s ease-in-out infinite reverse; z-index: 0; pointer-events: none;
}
.preview-root .pv-animated-bg {
  background-size: 200% 200%; animation: pv-grad-shift 22s ease-in-out infinite;
}
.preview-root .pv-float { animation: pv-float 6s ease-in-out infinite; }

.preview-root .pv-h {
  font-family: var(--font-display, "DM Sans"), system-ui, sans-serif;
  font-weight: 700; line-height: 0.95; letter-spacing: -0.03em;
}
.preview-root .pv-eyebrow {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  text-transform: uppercase; letter-spacing: 0.35em; font-size: 0.72rem;
}
.preview-root .pv-pill {
  display: inline-flex; align-items: center; gap: 0.6rem;
  padding: 1rem 1.6rem; border-radius: 999px;
  background: var(--pv-yellow); color: var(--pv-ink);
  font-weight: 700; letter-spacing: -0.01em; font-size: 1.05rem;
  transition: transform 250ms ease, background 250ms ease, gap 250ms ease;
  border: 2px solid var(--pv-ink);
  box-shadow: 6px 6px 0 var(--pv-ink);
}
.preview-root .pv-pill:hover { gap: 0.9rem; transform: translate(-2px,-2px); box-shadow: 8px 8px 0 var(--pv-ink); }
.preview-root .pv-pill.is-light:hover { background: #fff; }

.preview-root .pv-card {
  border-radius: 28px; padding: 2rem; position: relative; overflow: hidden;
  border: 2px solid var(--pv-ink); box-shadow: 8px 8px 0 var(--pv-ink);
  transition: transform 300ms ease, box-shadow 300ms ease;
}
.preview-root .pv-card:hover { transform: translate(-3px,-3px); box-shadow: 12px 12px 0 var(--pv-ink); }

.preview-root .pv-divider {
  position: absolute; left: 0; right: 0; width: 100%; height: 60px; z-index: 2; pointer-events: none;
}
.preview-root .pv-divider-top { top: -1px; }
.preview-root .pv-divider-bottom { bottom: -1px; transform: scaleY(-1); }

/* ----- Preview Header ----- */
.preview-root .pv-header {
  position: fixed; top: 0; left: 0; right: 0; z-index: 60;
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.5rem; gap: 1.25rem;
  transition: background-color 300ms ease, border-color 300ms ease, color 300ms ease, box-shadow 300ms ease;
  border-bottom: 2px solid transparent;
}
.preview-root .pv-header.is-solid {
  background: var(--pv-white); border-bottom-color: var(--pv-ink);
  box-shadow: 0 6px 0 -2px var(--pv-ink);
}
.preview-root .pv-header .pv-wordmark {
  font-family: var(--font-display, "DM Sans"), system-ui, sans-serif;
  font-weight: 900; letter-spacing: -0.04em; font-size: 1.6rem; line-height: 1;
}
.preview-root .pv-nav a { font-weight: 600; letter-spacing: -0.01em; padding: 0.5rem 0.9rem; border-radius: 999px; transition: background 200ms ease, color 200ms ease; }
.preview-root .pv-nav a:hover { background: var(--pv-yellow); color: var(--pv-ink); }
.preview-root .pv-header-pill {
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.7rem 1.2rem; border-radius: 999px; font-weight: 700;
  background: var(--pv-ink); color: var(--pv-yellow);
  border: 2px solid var(--pv-ink);
  transition: transform 200ms ease, background 200ms ease, color 200ms ease;
}
.preview-root .pv-header-pill:hover { background: var(--pv-yellow); color: var(--pv-ink); transform: translateY(-2px); }

/* ----- Preview Footer ----- */
.preview-root .pv-footer { background: var(--pv-ink); color: var(--pv-white); position: relative; overflow: hidden; }
.preview-root .pv-footer-marquee {
  white-space: nowrap; overflow: hidden; border-top: 2px solid var(--pv-yellow); border-bottom: 2px solid var(--pv-yellow);
  padding: 1.25rem 0;
}
.preview-root .pv-footer-marquee-track {
  display: inline-flex; gap: 3rem; padding-right: 3rem;
  animation: marquee 30s linear infinite;
}
.preview-root .pv-footer-mega {
  font-family: var(--font-display, "DM Sans"), system-ui, sans-serif;
  font-weight: 900; letter-spacing: -0.06em; line-height: 0.85;
  position: absolute; left: 0; right: 0; bottom: -0.15em;
  color: transparent; -webkit-text-stroke: 2px rgba(255,255,255,0.08);
  pointer-events: none; text-align: center; font-size: clamp(8rem, 28vw, 28rem);
}
.preview-root .pv-footer a { transition: color 200ms ease; }
.preview-root .pv-footer a:hover { color: var(--pv-yellow); }

@media (prefers-reduced-motion: reduce) {
  .preview-root .pv-orb, .preview-root .pv-orb-2, .preview-root .pv-animated-bg, .preview-root .pv-float { animation: none !important; }
  .preview-root { transition: none; }
}
`;

function HomePreviewRoute() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [scene, setScene] = useState<SceneTone>("white");
  const [locale, setLocale] = useState<PreviewLocale>("en");
  const copy = useMemo(() => getCopy(locale), [locale]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const scenes = root.querySelectorAll<HTMLElement>("[data-scene-tone]");
    const io = new IntersectionObserver(
      (entries) => {
        // pick the entry closest to viewport center
        let best: { tone: SceneTone; ratio: number } | null = null;
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const tone = (e.target as HTMLElement).dataset.sceneTone as SceneTone;
          if (best === null || e.intersectionRatio > best.ratio) {
            best = { tone, ratio: e.intersectionRatio };
          }
        });
        if (best !== null) setScene((best as { tone: SceneTone }).tone);
      },
      { threshold: [0.25, 0.5, 0.75], rootMargin: "-30% 0px -30% 0px" },
    );
    scenes.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: SCOPED_CSS }} />
      <div ref={rootRef} className="preview-root" data-scene={scene}>
        <PreviewHeader copy={copy} locale={locale} onToggleLocale={() => setLocale((l) => (l === "en" ? "es" : "en"))} />
        <PreviewHero copy={copy} />
        <PreviewMarquee />
        <PreviewPressStrip copy={copy} />
        <PreviewRealProblem />
        <PreviewIsThisYou copy={copy} />
        <PreviewPhases />
        <PreviewProof />
        <PreviewCaseRail />
        <PreviewQuestions />
        <PreviewProcessBand />
        <PreviewTestimonials copy={copy} />
        <PreviewHowWeWork copy={copy} />
        <PreviewPricingStrip copy={copy} />
        <PreviewLogos copy={copy} />
        <PreviewCTA copy={copy} />
        <PreviewFooter copy={copy} />
      </div>
    </>
  );
}

/* ---------- Sticker ---------- */
function PreviewSticker({
  src, alt = "", rotate = 0, parallax = 0.08, className, style, float = false,
}: {
  src: string; alt?: string; rotate?: number; parallax?: number;
  className?: string; style?: CSSProperties; float?: boolean;
}) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (reduced) return;
    let raf = 0;
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const delta = (window.innerHeight / 2 - center) * parallax;
      setOffset(delta);
    };
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, [parallax, reduced]);

  return (
    <div
      ref={ref}
      aria-hidden={alt ? undefined : true}
      className={cn("pointer-events-none select-none will-change-transform", float && "pv-float", className)}
      style={{
        transform: float ? undefined : `translate3d(0, ${offset}px, 0) rotate(${rotate + offset * 0.04}deg)`,
        ["--r" as any]: `${rotate}deg`,
        ...style,
      }}
    >
      <img src={src} alt={alt} className="h-full w-full object-contain drop-shadow-2xl" />
    </div>
  );
}

/* ---------- Curved Divider ---------- */
function CurvedDivider({ fill, position = "bottom" }: { fill: string; position?: "top" | "bottom" }) {
  return (
    <svg
      className={cn("pv-divider", position === "top" ? "pv-divider-top" : "pv-divider-bottom")}
      viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden
    >
      <path d="M0,0 L1440,0 L1440,40 C1080,80 360,80 0,40 Z" fill={fill} />
    </svg>
  );
}

/* ---------- 1. HERO ---------- */
function PreviewHero({ copy }: { copy: PreviewCopy }) {
  return (
    <section data-scene-tone="sky" className="pv-scene pv-scene-sky-hero min-h-dvh">
      <div className="relative z-10 mx-auto flex min-h-dvh max-w-[1400px] flex-col justify-center px-6 py-28 sm:px-10">
        <Reveal>
          <p className="pv-eyebrow text-[color:var(--pv-ink)]/70">◆ {copy.heroEyebrow}</p>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 items-center gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <h1 className="pv-h text-[color:var(--pv-ink)]" style={{ fontSize: "clamp(3rem, 9vw, 8.5rem)" }}>
              <Reveal delay={80}><span className="block">{copy.heroLine1}</span></Reveal>
              <Reveal delay={160}>
                <span className="block" style={{ color: "var(--pv-electric)" }}>
                  {copy.heroLine2}
                </span>
              </Reveal>
            </h1>
            <Reveal delay={260}>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-[color:var(--pv-ink)]/75">
                {copy.heroSub}
              </p>
            </Reveal>
            <Reveal delay={340}>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link to={copy.quoteHref} className="pv-pill">
                  {copy.heroCta} <ArrowRight className="h-5 w-5" />
                </Link>
                <div className="flex items-center gap-2 text-[color:var(--pv-ink)]/70" style={{ fontFamily: "ui-monospace" }}>
                  <ChevronDown className="h-4 w-4 animate-bounce" /> {copy.heroScroll}
                </div>
              </div>
            </Reveal>
          </div>
          <div className="relative md:col-span-5">
            <PreviewSticker src={zebraHero} rotate={-6} parallax={0.04}
              className="mx-auto h-[40vh] w-auto md:h-[60vh]"
              style={{ maxWidth: "min(80vw, 520px)" }} />
            <PreviewSticker src={airplaneUp} rotate={-14} parallax={0.12} float
              className="absolute -left-4 top-2 h-20 w-20 md:h-28 md:w-28" />
            <PreviewSticker src={accentLeaf} rotate={20} parallax={0.1} float
              className="absolute -bottom-4 right-2 h-16 w-16 md:h-24 md:w-24" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Header / Footer wrappers over shared chrome ---------- */

function PreviewHeader({
  copy,
  locale,
  onToggleLocale,
}: {
  copy: PreviewCopy;
  locale: PreviewLocale;
  onToggleLocale: () => void;
}) {
  const resources: ResourcesMenu = {
    pillarsLabel: copy.resourcesMenu.pillarsLabel,
    topicsLabel: copy.resourcesMenu.topicsLabel,
    pillars: copy.resourcesMenu.pillars.map((p) => ({ ...p })),
    topics: copy.resourcesMenu.topics.map((p) => ({ ...p })),
    featured: { ...copy.resourcesMenu.featured },
  };
  const altLocale: PreviewLocale = locale === "en" ? "es" : "en";
  return (
    <SharedPreviewHeader
      homeHref="/home-preview"
      nav={{
        work: { label: copy.nav.work.label, to: copy.nav.work.to },
        services: { label: copy.nav.services.label, to: copy.nav.services.to },
        pricing: { label: copy.nav.pricing.label, to: copy.nav.pricing.to },
        resources: { label: copy.nav.resources.label, to: copy.nav.resources.to },
        about: { label: copy.nav.about.label, to: copy.nav.about.to },
      }}
      servicesMenu={copy.servicesMenu}
      resourcesMenu={resources}
      quoteLabel={copy.getQuote}
      quoteHref={copy.quoteHref}
      currentLocale={locale}
      showLang
      altLocale={altLocale}
      onLangClick={onToggleLocale}
    />
  );
}

function PreviewFooter({ copy }: { copy: PreviewCopy }) {
  const columns: FooterColumn[] = [
    {
      heading: copy.lang === "ES" ? "Compañía" : "Company",
      items: [
        [copy.nav.about.label, copy.nav.about.to],
        [copy.nav.work.label, copy.nav.work.to],
        [copy.nav.resources.label, copy.nav.resources.to],
        [copy.lang === "ES" ? "Contacto" : "Contact", copy.lang === "ES" ? "/es/contacto" : "/contact"],
      ],
    },
    {
      heading: copy.lang === "ES" ? "Servicios" : "Services",
      items: copy.servicesMenu.slice(0, 4),
    },
    {
      heading: copy.lang === "ES" ? "Alcance" : "Reach",
      items: [
        ["USA", copy.lang === "ES" ? "/es/contacto" : "/contact"],
        ["México", copy.lang === "ES" ? "/es/contacto" : "/contact"],
        ["España", copy.lang === "ES" ? "/es/contacto" : "/contact"],
        ["Latam", copy.lang === "ES" ? "/es/contacto" : "/contact"],
      ],
    },
  ];
  return (
    <SharedPreviewFooter
      homeHref="/home-preview"
      columns={columns}
      tagline={
        copy.lang === "ES"
          ? "Shopify Select Partner desde 2015. Rendimiento, CRO e ingeniería para marcas DTC en América y Europa."
          : "Shopify Select Partner since 2015. Performance, CRO, and technical SEO for DTC brands across the Americas and Europe."
      }
      quoteLabel={copy.getQuote}
      quoteHref={copy.quoteHref}
      marqueeText={copy.lang === "ES" ? "Movamos los números." : "Let's move the numbers."}
      bottomLinks={[
        [copy.lang === "ES" ? "Privacidad" : "Privacy", copy.lang === "ES" ? "/es/privacidad" : "/privacy"],
        [copy.lang === "ES" ? "Términos" : "Terms", copy.lang === "ES" ? "/es/terminos" : "/terms"],
        [copy.lang === "ES" ? "Portal de cliente" : "Client portal", "/portal"],
        [copy.lang === "ES" ? "Acceso agencias" : "Agency login", "/agency-login"],
        ["Admin", "/admin"],
      ]}
      rightsText="Shopify Select Partner."
    />
  );
}
/* ---------- 2. MARQUEE (yellow band, two rows opposite) ---------- */
function PreviewMarquee() {
  const reduced = useReducedMotion();
  const top = [
    { t: "Shopify Plus migrations", g: accentLeaf },
    { t: "Conversion engineering", g: urgentBolt },
    { t: "Technical SEO", g: arrowRight },
    { t: "Hydrogen & headless", g: cloudDownload },
    { t: "Theme architecture", g: airplaneUp },
  ];
  const bottom = [
    { t: "750+ Shopify engagements completed", g: handPointer },
    { t: "USA · Mexico · Spain", g: airplaneDown },
    { t: "10 years on Shopify", g: urgentExclam },
    { t: "Fixed scope · Upfront pricing", g: arrowRight },
    { t: "Real engineers, no juniors", g: accentLeaf },
  ];
  const row = (items: typeof top, dir: "fwd" | "rev") => (
    <div className="flex w-full overflow-hidden" aria-hidden>
      <div
        className="flex shrink-0 items-center gap-12 pr-12"
        style={{
          animation: reduced ? "none" : `${dir === "fwd" ? "marquee" : "pv-marquee-rev"} 90s linear infinite`,
        }}
      >
        {[...items, ...items, ...items].map((it, i) => (
          <span key={i} className="flex shrink-0 items-center gap-6">
            <span className="pv-h" style={{ fontSize: "clamp(2rem, 5.5vw, 4.5rem)" }}>{it.t}</span>
            <img src={it.g} alt="" aria-hidden className="h-10 w-10 shrink-0" />
          </span>
        ))}
      </div>
    </div>
  );
  return (
    <section data-scene-tone="yellow" className="pv-scene pv-scene-yellow relative border-y-4 border-[color:var(--pv-ink)] py-16 sm:py-20">
      <div className="flex flex-col gap-4">
        {row(top, "fwd")}
        {row(bottom, "rev")}
      </div>
    </section>
  );
}

/* ---------- Press strip (right after hero) ---------- */
function PreviewPressStrip({ copy }: { copy: PreviewCopy }) {
  return (
    <section className="pv-scene pv-scene-white border-b-2 border-[color:var(--pv-ink)]/10">
      <div className="mx-auto max-w-[1400px] px-6 py-12 sm:px-10">
        <p className="pv-eyebrow text-center text-[color:var(--pv-ink)]/60">◆ {copy.featured}</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-14 gap-y-6 opacity-80">
          <img src={forbesLogo} alt="Forbes" className="h-7 w-auto object-contain" />
          <img src={apLogo} alt="Associated Press" className="h-8 w-auto object-contain" />
          <img src={lavanguardiaLogo} alt="La Vanguardia" className="h-6 w-auto object-contain" />
          <img src={europapressLogo} alt="Europa Press" className="h-9 w-auto object-contain" />
          <img src={techbehemothsLogo} alt="TechBehemoths" className="h-8 w-auto object-contain" />
        </div>
      </div>
    </section>
  );
}

/* ---------- 3. PHASES ---------- */
const PHASES = [
  {
    n: "01", title: "Migrations & replatforms",
    body: "Shopify Plus migrations with zero downtime. DNS cutovers with rollback ready, SEO redirects in place, real data mirrored before launch.",
    image: shopifyDev, sticker: zebraSteps, tint: "var(--pv-sky)", rotate: -3,
  },
  {
    n: "02", title: "Theme dev & conversion",
    body: "Custom themes and CRO tuned to your funnel. Fixed scope, shipped in 2–4 weeks, with measurable lift you can actually point to.",
    image: shopifyCart, sticker: geoRoadmap, tint: "var(--pv-yellow)", rotate: 4,
  },
  {
    n: "03", title: "Technical SEO & AI",
    body: "Core Web Vitals, structured data, GEO readiness for AI search. Engineering-grade audits, not surface-level checklists.",
    image: shopifyAi, sticker: geoCitations, tint: "var(--pv-electric)", rotate: -2,
  },
];

function PreviewPhases() {
  return (
    <section data-scene-tone="white" className="pv-scene pv-scene-white">
      <CurvedDivider position="top" fill="#FFE94A" />
      <div className="mx-auto max-w-[1400px] px-6 pt-32 pb-28 sm:px-10 sm:pt-40 sm:pb-36">
        <Reveal>
          <p className="pv-eyebrow text-[color:var(--pv-electric)]">◆ What we do</p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="pv-h mt-6 max-w-5xl" style={{ fontSize: "clamp(2.25rem, 6vw, 6rem)" }}>
            Three things, <span style={{ color: "var(--pv-electric)" }}>done properly.</span>
          </h2>
        </Reveal>
        <div className="mt-20 flex flex-col gap-24">
          {PHASES.map((p, i) => {
            const reverse = i % 2 === 1;
            return (
              <Reveal key={p.n} delay={i * 80}>
                <div className={cn("relative grid grid-cols-1 items-center gap-10 md:grid-cols-12", reverse && "md:[direction:rtl]")}>
                  <div className="md:col-span-6 md:[direction:ltr]">
                    <div
                      className="pv-card"
                      style={{ background: p.tint, transform: `rotate(${p.rotate}deg)` }}
                    >
                      <img src={p.image} alt="" className="mx-auto h-64 w-auto md:h-80" />
                      <PreviewSticker src={p.sticker} rotate={-p.rotate * 4} parallax={0.05} float
                        className="absolute -bottom-6 -right-6 h-24 w-24 md:h-32 md:w-32" />
                    </div>
                  </div>
                  <div className="md:col-span-6 md:[direction:ltr]">
                    <div className="flex items-baseline gap-4">
                      <span className="pv-h text-[color:var(--pv-electric)]/30" style={{ fontSize: "clamp(4rem, 9vw, 9rem)" }}>{p.n}</span>
                    </div>
                    <h3 className="pv-h mt-2" style={{ fontSize: "clamp(1.75rem, 4vw, 4rem)" }}>{p.title}</h3>
                    <p className="mt-6 max-w-xl text-base leading-relaxed text-[color:var(--pv-ink)]/70 sm:text-lg">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- 4. PROOF ---------- */
const PROOF = [
  { value: "750+", label: "Shopify engagements completed" },
  { value: "35", label: "Markets live" },
  { value: "4.9★", label: "Partner rating" },
  { value: "10 yrs", label: "Shipping Shopify" },
];

function PreviewProof() {
  return (
    <section data-scene-tone="navy" className="pv-scene pv-scene-navy pv-animated-bg">
      <CurvedDivider position="top" fill="#f2f2f0" />
      <div className="pv-orb" style={{ inset: "-20% auto auto -10%" }} />
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 pt-32 pb-28 sm:px-10 sm:pt-40 sm:pb-36">
        <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal>
              <p className="pv-eyebrow text-white/70">◆ The receipts</p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="pv-h mt-6 text-white" style={{ fontSize: "clamp(2.25rem, 6vw, 6rem)" }}>
                The numbers <span style={{ color: "var(--pv-sky)" }}>that matter.</span>
              </h2>
            </Reveal>
          </div>
          <div className="relative md:col-span-5">
            <PreviewSticker src={zebraPortrait} rotate={6} parallax={0.05}
              className="ml-auto h-48 w-auto md:h-64" />
            <PreviewSticker src={geoScore} rotate={-10} parallax={0.1} float
              className="absolute -left-2 bottom-0 h-24 w-24 md:h-32 md:w-32" />
          </div>
        </div>
        <div className="mt-16 grid grid-cols-2 gap-5 md:grid-cols-4">
          {PROOF.map((s, i) => (
            <Reveal key={s.value} delay={i * 80}>
              <div className="pv-card h-full" style={{ background: i % 2 === 0 ? "var(--pv-electric)" : "var(--pv-sky)", color: i % 2 === 0 ? "#fff" : "var(--pv-ink)" }}>
                <div className="pv-h" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>{s.value}</div>
                <div className="mt-3 text-sm font-medium uppercase tracking-wider opacity-80">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 5. QUESTIONS ---------- */
const QUESTIONS = [
  { q: "How long does a migration take?",
    a: "Most Shopify Plus migrations ship in 4–8 weeks end-to-end. We mirror your live data, QA on a private URL, and cut over DNS in a scheduled low-traffic window with rollback ready." },
  { q: "What if I'm locked into my current theme?",
    a: "We've inherited every kind of Shopify setup, bespoke themes from agencies that vanished, ThemeForest builds with no docs, app stacks duct-taped together. Our first deliverable is always an audit. From there: work inside, fork cleanly, or replatform, your call." },
  { q: "What does it cost?",
    a: "Performance projects from $1,500. CRO and Technical SEO from $2,000. Custom builds from $5,000. Ongoing Shopify Growth & Engineering retainers from $5,000/mo; Shopify Plus engineering retainers from $9,000/mo. Focused entry plans available from $1,495/mo for small, clearly defined backlogs. Fixed scope, priced upfront before we start." },
];

function PreviewQuestions() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section data-scene-tone="sky" className="pv-scene pv-scene-sky">
      <CurvedDivider position="top" fill="#1f1f1f" />
      <div className="relative mx-auto max-w-[1400px] px-6 pt-32 pb-28 sm:px-10 sm:pt-40 sm:pb-36">
        <div className="grid grid-cols-1 items-start gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Reveal><p className="pv-eyebrow">◆ Questions, answered</p></Reveal>
            <Reveal delay={80}>
              <h2 className="pv-h mt-6" style={{ fontSize: "clamp(2.25rem, 6vw, 6rem)" }}>
                Straight <span style={{ color: "var(--pv-electric)" }}>answers.</span>
              </h2>
            </Reveal>
            <div className="relative mt-10 hidden md:block">
              <PreviewSticker src={zebraFaq} rotate={-8} parallax={0.07}
                className="h-56 w-56" />
              <PreviewSticker src={airplaneLegend} rotate={10} parallax={0.1} float
                className="absolute -right-4 top-10 h-24 w-24" />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="divide-y-2 divide-[color:var(--pv-ink)]/30 border-y-2 border-[color:var(--pv-ink)]/30">
              {QUESTIONS.map((item, i) => {
                const isOpen = open === i;
                return (
                  <div key={i}>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      className="group flex w-full items-center justify-between gap-6 py-8 text-left transition-opacity hover:opacity-80"
                    >
                      <span className="pv-h" style={{ fontSize: "clamp(1.25rem, 2.6vw, 2.25rem)" }}>{item.q}</span>
                      <span
                        className={cn(
                          "flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-[color:var(--pv-ink)] transition-transform duration-300",
                          isOpen ? "rotate-45 bg-[color:var(--pv-electric)] text-white" : "bg-white",
                        )}
                      >
                        <Plus className="h-5 w-5" />
                      </span>
                    </button>
                    <div
                      className={cn(
                        "grid overflow-hidden transition-all duration-500",
                        isOpen ? "grid-rows-[1fr] pb-8 opacity-100" : "grid-rows-[0fr] opacity-0",
                      )}
                    >
                      <p className="min-h-0 max-w-2xl text-base leading-relaxed text-[color:var(--pv-ink)]/80 sm:text-lg">{item.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- 6. LOGOS ---------- */
const LOGOS: Array<[string, string]> = [
  [boletaje, "Boletaje"], [butcherjames, "Butcher James"], [boxchois, "Box Chois"],
  [fashionablecanes, "Fashionable Canes"], [medshift, "Medshift"], [classy, "Classy Walking Canes"],
  [cleveland, "Cleveland Kitchen"], [pokerpower, "Poker Power"], [redondo, "Redondo Brand"],
  [selfcare, "Self Care Is For Everyone"], [threadseed, "Threadseed"], [oomame, "Oomame"],
];

function PreviewLogos({ copy }: { copy: PreviewCopy }) {
  return (
    <section data-scene-tone="white" className="pv-scene pv-scene-white">
      <CurvedDivider position="top" fill="#7BB7FF" />
      <div className="mx-auto max-w-[1400px] px-6 pt-32 pb-28 sm:px-10 sm:pt-40 sm:pb-36">
        <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12">
          <div className="md:col-span-8">
            <Reveal><p className="pv-eyebrow text-[color:var(--pv-electric)]">◆ {copy.rosterEyebrow}</p></Reveal>
            <Reveal delay={80}>
              <h2 className="pv-h mt-6" style={{ fontSize: "clamp(2.25rem, 6vw, 6rem)" }}>
                <span style={{ color: "var(--pv-electric)" }}>{copy.rosterTitle}</span>
              </h2>
            </Reveal>
          </div>
          <div className="relative hidden md:col-span-4 md:block">
            <PreviewSticker src={zebraSimple} rotate={8} parallax={0.05} className="ml-auto h-32 w-auto" />
          </div>
        </div>
        <Reveal delay={120}>
          <div className="mt-16 rounded-3xl border-2 border-[color:var(--pv-ink)] bg-white py-10">
            <Marquee durationSeconds={50}>
              {LOGOS.map(([src, name]) => (
                <img
                  key={name}
                  src={src}
                  alt={name}
                  loading="lazy"
                  decoding="async"
                  className="h-12 w-auto object-contain md:h-14"
                />
              ))}
            </Marquee>
          </div>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-16 flex flex-col items-start gap-6 border-t-2 border-[color:var(--pv-ink)]/15 pt-10 sm:flex-row sm:items-center sm:justify-between">
            <p className="pv-eyebrow text-[color:var(--pv-ink)]/60">◆ As featured in</p>
            <div className="flex flex-wrap items-center gap-10 opacity-80">
              <img src={forbesLogo} alt="Forbes" className="h-7 w-auto object-contain" />
              <img src={apLogo} alt="Associated Press" className="h-7 w-auto object-contain" />
              <img src={techbehemothsLogo} alt="TechBehemoths" className="h-8 w-auto object-contain" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- How We Work (4 steps) ---------- */
function PreviewHowWeWork({ copy }: { copy: PreviewCopy }) {
  return (
    <section data-scene-tone="white" className="pv-scene pv-scene-white">
      <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32">
        <Reveal>
          <p className="pv-eyebrow text-[color:var(--pv-electric)]">◆ {copy.howEyebrow}</p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="pv-h mt-6 max-w-3xl" style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}>
            {copy.howTitle}
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {copy.howSteps.map(([title, body], i) => (
            <Reveal key={title} delay={120 + i * 60}>
              <div className="h-full rounded-3xl border-2 border-[color:var(--pv-ink)] bg-white p-7" style={{ boxShadow: "6px 6px 0 var(--pv-ink)" }}>
                <div className="pv-h text-[color:var(--pv-electric)]" style={{ fontSize: "2.5rem" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="pv-h mt-4 text-xl leading-tight">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[color:var(--pv-ink)]/75">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Pricing strip ---------- */
function PreviewPricingStrip({ copy }: { copy: PreviewCopy }) {
  const intents: ScopeIntentId[] = ["revenue_fix", "ongoing", "diagnostic"];
  const locale: "en" | "es" = copy.lang === "ES" ? "es" : "en";
  return (
    <section data-scene-tone="white" className="pv-scene pv-scene-white">
      <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Reveal><p className="pv-eyebrow text-[color:var(--pv-electric)]">◆ {copy.pricingEyebrow}</p></Reveal>
            <Reveal delay={80}>
              <h2 className="pv-h mt-6 max-w-2xl" style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}>
                {copy.pricingTitle}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={140}>
            <Link to={copy.nav.pricing.to} className="pv-eyebrow text-[color:var(--pv-electric)] hover:underline">
              {copy.pricingSeeAll}
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {copy.pricingCards.map(([title, price, body], i) => (
            <Reveal key={title} delay={120 + i * 80}>
              <Link
                to={copy.quoteHref}
                search={quoteSearch({ intent: intents[i], source: "home-preview-pricing" })}
                className="group block h-full rounded-3xl border-2 border-[color:var(--pv-ink)] bg-white p-8 transition-transform"
                style={{ boxShadow: "8px 8px 0 var(--pv-ink)" }}
              >
                <p className="pv-eyebrow text-[color:var(--pv-ink)]/60">{title}</p>
                <div className="mt-5 pv-h" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                  <span className="relative inline-block">
                    <span className="relative z-10">{price}</span>
                    <span aria-hidden className="absolute bottom-1 left-0 right-0 z-0 h-3" style={{ background: "var(--pv-yellow)" }} />
                  </span>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-[color:var(--pv-ink)]/75">{body}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[color:var(--pv-electric)] group-hover:gap-3 transition-all">
                  {copy.getQuote} <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={400}>
          <p className="mt-10 text-center text-sm text-[color:var(--pv-ink)]/65">{copy.pricingFooter}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 7. CTA ---------- */
function PreviewCTA({ copy }: { copy: PreviewCopy }) {
  return (
    <section data-scene-tone="electric" className="pv-scene pv-scene-electric pv-animated-bg min-h-[80vh]">
      <CurvedDivider position="top" fill="#f2f2f0" />
      <div className="pv-orb" />
      <div className="pv-orb-2" />
      <div className="relative z-10 mx-auto flex min-h-[80vh] max-w-[1400px] flex-col justify-center px-6 pt-32 pb-28 sm:px-10">
        <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-12">
          <div className="md:col-span-8">
            <Reveal><p className="pv-eyebrow text-white/80">◆ The next move</p></Reveal>
            <h2 className="pv-h mt-6 text-white" style={{ fontSize: "clamp(2.75rem, 9vw, 8.5rem)" }}>
              <Reveal delay={80}><span className="block">Let's build something</span></Reveal>
              <Reveal delay={160}><span className="block" style={{ color: "var(--pv-yellow)" }}>that converts.</span></Reveal>
            </h2>
            <Reveal delay={260}>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-white/85">
                Send us your store. We'll come back with a scope and a price in 48 hours.
              </p>
            </Reveal>
            <Reveal delay={340}>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link to={copy.quoteHref} className="pv-pill">
                  {copy.heroCta} <ArrowRight className="h-5 w-5" />
                </Link>
                <img src={urgentBolt} alt="" aria-hidden className="h-12 w-12 pv-float" />
              </div>
            </Reveal>
          </div>
          <div className="relative md:col-span-4">
            <PreviewSticker src={ctaZebra} rotate={6} parallax={0.04}
              className="mx-auto h-[40vh] w-auto md:h-[50vh]" />
            <PreviewSticker src={giraffe} rotate={-10} parallax={0.1} float
              className="absolute -left-2 -top-4 h-24 w-24 md:h-32 md:w-32" />
          </div>
        </div>
      </div>
    </section>
  );
}


/* ---------- Real Problem ---------- */
const PAIN = [
  { t: "Slow pages reduce conversion", c: "var(--pv-electric)", ink: "#fff" },
  { t: "Poor UX leaks revenue on PDPs", c: "var(--pv-sky)", ink: "var(--pv-ink)" },
  { t: "Technical SEO blocks organic growth", c: "var(--pv-yellow)", ink: "var(--pv-ink)" },
  { t: "Too many apps create instability", c: "var(--pv-navy)", ink: "#fff" },
];
function PreviewRealProblem() {
  return (
    <section data-scene-tone="white" className="pv-scene pv-scene-white">
      <CurvedDivider position="top" fill="#FFE94A" />
      <div className="mx-auto max-w-[1400px] px-6 pt-32 pb-28 sm:px-10 sm:pt-40 sm:pb-36">
        <Reveal><p className="pv-eyebrow text-[color:var(--pv-electric)]">◆ The real problem</p></Reveal>
        <Reveal delay={80}>
          <h2 className="pv-h mt-6 max-w-5xl" style={{ fontSize: "clamp(2.5rem, 7vw, 6.5rem)" }}>
            Most Shopify stores don't have a <span style={{ color: "var(--pv-electric)" }}>design problem.</span>
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[color:var(--pv-ink)]/75">
            They have performance, conversion, and technical debt issues that quietly limit growth.
            We focus on fixing what actually impacts your numbers.
          </p>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PAIN.map((p, i) => (
            <Reveal key={p.t} delay={i * 70}>
              <div className="pv-card h-full" style={{ background: p.c, color: p.ink }}>
                <div className="pv-h" style={{ fontSize: "clamp(1.3rem, 2vw, 1.8rem)" }}>{p.t}</div>
                <div className="mt-8 text-xs uppercase tracking-[0.35em] opacity-70">0{i + 1}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PreviewIsThisYou({ copy }: { copy: PreviewCopy }) {
  return (
    <section data-scene-tone="white" className="pv-scene pv-scene-white">
      <div className="mx-auto max-w-[1400px] px-6 pb-28 sm:px-10 sm:pb-36">
        <Reveal>
          <p className="pv-eyebrow text-[color:var(--pv-ink)]/55">{copy.isThisYou.eyebrow}</p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="pv-h mt-6 max-w-5xl" style={{ fontSize: "clamp(2.25rem, 6vw, 5.25rem)" }}>
            {copy.isThisYou.title}
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <div className="mt-6">
            <span className="inline-flex items-center rounded-full border-2 border-[color:var(--pv-ink)] bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-[color:var(--pv-ink)] shadow-[3px_3px_0_var(--pv-ink)]">
              {copy.isThisYou.qualifier}
            </span>
          </div>
        </Reveal>
        <div className="mt-14 border-t border-[color:var(--pv-ink)]/15">
          {copy.isThisYou.rows.map((row, i) => (
            <Reveal key={row} delay={i * 60}>
              <div className="flex items-center justify-between gap-6 border-b border-[color:var(--pv-ink)]/15 py-7 sm:py-8">
                <div className="pv-h text-[color:var(--pv-ink)]" style={{ fontSize: "clamp(1.25rem, 2.6vw, 2rem)" }}>
                  {row}
                </div>
                <div className="shrink-0 text-xs font-semibold uppercase tracking-[0.35em] text-[color:var(--pv-ink)]/40">
                  0{i + 1}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Case Rail ---------- */
const CASES = [
  { name: "Fashionable Canes", tag: "CRO & SEO", img: fashionablecanes,
    stats: [["+350%", "ROI lift"], ["+180%", "Organic"], ["#1", "Cat SERPs"]] as const },
  { name: "BoxChois", tag: "Performance", img: boxchois,
    stats: [["4.2→1.5s", "LCP"], ["+22%", "Conv."], ["0", "Downtime"]] as const },
  { name: "Oomame", tag: "CRO · Experimentation", img: oomame,
    stats: [["+22%", "Checkout"], ["90d", "Sprint"], ["12", "Wins"]] as const },
  { name: "MedShift", tag: "B2B Plus", img: medshift,
    stats: [["6 figs", "Q1 B2B"], ["Net 30", "Terms"], ["3 tiers", "Pricing"]] as const },
  { name: "Cleveland Kitchen", tag: "Theme · Architecture", img: cleveland,
    stats: [["Rebuilt", "Theme"], ["100%", "Docs"], ["Trained", "Team"]] as const },
];
function PreviewCaseRail() {
  return (
    <section data-scene-tone="electric" className="pv-scene pv-scene-electric pv-animated-bg">
      <CurvedDivider position="top" fill="#1f1f1f" />
      <div className="mx-auto max-w-[1400px] px-6 pt-32 pb-12 sm:px-10 sm:pt-40">
        <Reveal><p className="pv-eyebrow text-white/80">◆ Receipts in production</p></Reveal>
        <Reveal delay={80}>
          <h2 className="pv-h mt-6 max-w-5xl text-white" style={{ fontSize: "clamp(2.25rem, 6vw, 6rem)" }}>
            We don't just redesign stores. <span style={{ color: "var(--pv-yellow)" }}>We move the numbers.</span>
          </h2>
        </Reveal>
      </div>
      <div className="pb-28 sm:pb-36">
        <div className="flex snap-x snap-mandatory gap-6 overflow-x-auto px-6 pb-6 sm:px-10">
          {CASES.map((c, i) => (
            <div
              key={c.name}
              className="pv-card snap-start shrink-0"
              style={{ background: i % 2 === 0 ? "#fff" : "var(--pv-yellow)", color: "var(--pv-ink)", width: "min(80vw, 460px)" }}
            >
              <div className="flex aspect-[4/3] items-center justify-center rounded-2xl border-2 border-[color:var(--pv-ink)] bg-white p-8">
                <img src={c.img} alt={c.name} className="max-h-32 w-auto object-contain" />
              </div>
              <div className="mt-5 text-xs uppercase tracking-[0.3em] opacity-70">{c.tag}</div>
              <h3 className="pv-h mt-2" style={{ fontSize: "clamp(1.5rem, 2.4vw, 2rem)" }}>{c.name}</h3>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {c.stats.map(([v, l]) => (
                  <div key={l} className="rounded-xl border-2 border-[color:var(--pv-ink)] p-3 text-center">
                    <div className="pv-h" style={{ fontSize: "clamp(1rem, 1.6vw, 1.4rem)" }}>{v}</div>
                    <div className="mt-1 text-[0.65rem] uppercase tracking-wider opacity-70">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Process Band ---------- */
const STEPS = [
  { n: "01", t: "You tell us what's not working." },
  { n: "02", t: "We review your store and identify the highest-impact fixes." },
  { n: "03", t: "We send a clear scope and pricing, usually within 48 hours." },
];
function PreviewProcessBand() {
  return (
    <section data-scene-tone="yellow" className="pv-scene pv-scene-yellow">
      <CurvedDivider position="top" fill="#7BB7FF" />
      <div className="mx-auto max-w-[1400px] px-6 pt-32 pb-28 sm:px-10 sm:pt-40 sm:pb-36">
        <Reveal><p className="pv-eyebrow">◆ From first message to clear scope</p></Reveal>
        <Reveal delay={80}>
          <h2 className="pv-h mt-6 max-w-4xl" style={{ fontSize: "clamp(2.5rem, 7vw, 7rem)" }}>
            Usually within <span style={{ color: "var(--pv-electric)" }}>48 hours.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 80}>
              <div className="pv-card h-full" style={{ background: "#fff" }}>
                <div className="pv-h text-[color:var(--pv-electric)]" style={{ fontSize: "clamp(4rem, 8vw, 7rem)" }}>{s.n}</div>
                <p className="pv-h mt-4" style={{ fontSize: "clamp(1.25rem, 2vw, 1.8rem)" }}>{s.t}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={300}>
          <div className="mt-12">
            <Link to="/get-a-quote" className="pv-pill">
              Send us your store <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Testimonials (real reviews) ---------- */
function PreviewTestimonials({ copy }: { copy: PreviewCopy }) {
  const featured = REVIEWS.slice(0, 9);
  return (
    <section data-scene-tone="navy" className="pv-scene pv-scene-navy">
      <CurvedDivider position="top" fill="#f2f2f0" />
      <div className="mx-auto max-w-[1400px] px-6 pt-32 pb-28 sm:px-10 sm:pt-40 sm:pb-36">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Reveal>
              <p className="pv-eyebrow text-white/70">◆ {copy.reviewsEyebrow}</p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="pv-h mt-6 text-white" style={{ fontSize: "clamp(2.25rem, 6vw, 6rem)" }}>
                {copy.reviewsTitle}
              </h2>
            </Reveal>
            <div className="mt-6 flex items-center gap-3">
              <div role="img" className="flex items-center gap-1" aria-label={`${REVIEWS_META.rating} out of 5`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[color:var(--pv-yellow)] text-[color:var(--pv-yellow)]" aria-hidden="true" />
                ))}
              </div>
              <span className="pv-h text-white" style={{ fontSize: "1.25rem" }}>{REVIEWS_META.rating}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-white/60">· {REVIEWS_META.count} reviews</span>
            </div>
          </div>
          <a
            href={REVIEWS_META.partnerUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-[color:var(--pv-yellow)]"
          >
            {copy.reviewsAll} <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {featured.map((r, i) => (
            <Reveal key={`${r.brand}-${i}`} delay={(i % 6) * 60}>
              <figure className="flex h-full flex-col rounded-2xl border-2 border-[color:var(--pv-ink)] bg-white p-6 text-[color:var(--pv-ink)] shadow-[6px_6px_0_var(--pv-ink)]">
                <div className="flex items-center gap-1" aria-hidden>
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed">
                  &ldquo;{r.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-5 border-t-2 border-[color:var(--pv-ink)]/10 pt-4">
                  <div className="pv-h" style={{ fontSize: "1rem" }}>{r.brand}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.2em] opacity-60">{r.category}</div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

